/** Live booking GST. Mirrors the server order in rcm_gst_service. */

export const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z]\dZ[A-Z\d]$/

const FALLBACK = { sgst: 2.5, cgst: 2.5, igst: 5 }

export function classifyGstin(value, verificationOn, lookup) {
  const gstin = String(value || '').trim().toUpperCase()
  if (!gstin) return { status: 'empty', gstin: '' }
  if (!GSTIN_RE.test(gstin)) return { status: 'invalid', gstin }
  if (!verificationOn) return { status: 'registered', gstin }
  if (lookup && lookup.status === 'done') {
    const data = lookup.data || {}
    if (data.valid && data.active) return { status: 'registered', gstin }
    return { status: 'invalid', gstin }
  }
  // Still checking, or the lookup failed: a format-valid GSTIN stays registered.
  return { status: 'registered', gstin }
}

function ratesFor(rcm, sameState, useFallback) {
  if (sameState) {
    let sgst = Number.parseFloat(rcm?.sgst) || 0
    let cgst = Number.parseFloat(rcm?.cgst) || 0
    if (useFallback && sgst === 0 && cgst === 0) {
      sgst = FALLBACK.sgst
      cgst = FALLBACK.cgst
    }
    return { sgst, cgst, igst: 0 }
  }
  let igst = Number.parseFloat(rcm?.igst) || 0
  if (useFallback && igst === 0) igst = FALLBACK.igst
  return { sgst: 0, cgst: 0, igst }
}

function hubSameState(senderState, receiverState) {
  const from = String(senderState || '').trim().toLowerCase()
  const to = String(receiverState || '').trim().toLowerCase()
  return !!(from && to && from === to)
}

/**
 * override true means the form must use these percentages (freight base).
 * override false means manual percentages on the discounted subtotal.
 */
export function deriveBookingGst({
  rcmEnabled,
  rcm,
  senderGst,
  receiverGst,
  mode,
  companyGst,
  senderState,
  receiverState,
  verificationOn,
  senderLookup,
  receiverLookup,
}) {
  const sender = classifyGstin(senderGst, verificationOn, senderLookup)
  const receiver = classifyGstin(receiverGst, verificationOn, receiverLookup)
  const senderPays = ['paid', 'paid_balance'].includes(mode)
  const billing = senderPays ? sender : receiver
  const companyNumber = String(companyGst || '').trim().toUpperCase()

  if (!rcmEnabled) {
    return {
      override: false,
      gstBase: 'subtotal',
      includeInTotal: true,
      gstPayer: null,
      payerLabel: null,
      sgst: 0,
      cgst: 0,
      igst: 0,
    }
  }

  const companyLiable = (useSettingsRates) => {
    const same = hubSameState(senderState, receiverState)
    const applied = ratesFor(useSettingsRates ? rcm : FALLBACK, same, true)
    return {
      override: true,
      gstBase: 'freight',
      includeInTotal: true,
      gstPayer: companyNumber || null,
      payerLabel: 'Company',
      ...applied,
    }
  }

  if (billing.status === 'invalid') {
    return companyLiable(true)
  }

  if (sender.status === 'registered' && receiver.status === 'registered') {
    const same = sender.gstin.slice(0, 2) === receiver.gstin.slice(0, 2)
    return {
      override: true,
      gstBase: 'freight',
      includeInTotal: false,
      gstPayer: senderPays ? sender.gstin : receiver.gstin,
      payerLabel: senderPays ? 'Sender' : 'Receiver',
      ...ratesFor(rcm, same, false),
    }
  }

  if (sender.status === 'registered' || receiver.status === 'registered') {
    const party = sender.status === 'registered' ? sender : receiver
    const companyCode = companyNumber.slice(0, 2)
    const partyCode = party.gstin.slice(0, 2)
    const same = partyCode.length === 2 && companyCode.length === 2 && partyCode === companyCode
    return {
      override: true,
      gstBase: 'freight',
      includeInTotal: false,
      gstPayer: party.gstin,
      payerLabel: sender.status === 'registered' ? 'Sender' : 'Receiver',
      ...ratesFor(rcm, same, false),
    }
  }

  return companyLiable(true)
}
